import { useState } from "react";
import { OAUTH, OAUTH_PROVIDERS, type OAuthProvider } from "@oshimap/config";
import { ep, session } from "../../lib/api";
import { Button } from "../../components/ui";
import "../visit/visit.css";
import { useDialog } from "../../lib/use-dialog";

type Step = "email" | "password" | "code" | "name";

/**
 * FR-ENT-02: 진입 시 강제 로그인 화면을 두지 않는다. 행동 시점에만 뜬다.
 * N5: 로그인 성공 시 보류된 동작이 Session 에서 자동 재개된다.
 * OAuth 는 Native 계약이 정해진 뒤 (OD-004 / AC-05). 지금은 이메일 경로만 실제로 동작한다.
 */
const SOCIAL_LABEL: Record<OAuthProvider, string> = { naver: "네이버", kakao: "카카오", google: "구글" };

export function AuthSheet({ reason, onClose }: { reason: string; onClose: () => void }) {
  const dialog = useDialog(() => { session.cancelPending(); onClose(); });

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fail = (e: unknown) => setError(e instanceof Error ? e.message : "잠시 후 다시 시도해 주세요.");

  const submitEmail = async () => {
    setBusy(true); setError(null);
    try {
      const { available } = await ep.checkEmail(email.trim());
      if (available) {
        const r = await ep.requestEmailVerification(email.trim());
        setVerificationId(r.verificationId);
        setDevCode(r.devCode ?? null);
        setStep("code");
      } else {
        setStep("password");
      }
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const submitPassword = async () => {
    setBusy(true); setError(null);
    try {
      const r = await ep.login(email.trim(), password);
      await session.signIn(r.token, r.user);
      onClose();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const submitCode = async () => {
    setBusy(true); setError(null);
    try {
      const r = await ep.confirmEmailVerification(verificationId, code.trim());
      setVerificationToken(r.verificationToken);
      setStep("name");
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const submitName = async () => {
    setBusy(true); setError(null);
    try {
      const r = await ep.register({
        email: email.trim(), password, passwordConfirm: password,
        displayName: displayName.trim(), verificationToken,
      });
      await session.signIn(r.token, r.user);
      onClose();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  return (
    <div ref={dialog.ref} className="om-visit" role="dialog" aria-modal="true" aria-label="로그인">
      <div className="om-visit__form">
        <h2 className="om-visit__title">{reason}</h2>
        <p className="om-visit__note">로그인하면 하려던 동작을 이어서 진행합니다.</p>

        {step === "email" ? (
          <form onSubmit={(e) => { e.preventDefault(); void submitEmail(); }}>
            <label className="om-field"><span>이메일</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                autoComplete="email" required placeholder="fan@example.com" /></label>
            <Button type="submit" variant="primary" block disabled={busy || !email.trim()}>
              {busy ? "확인하는 중…" : "계속하기"}
            </Button>
          </form>
        ) : step === "password" ? (
          <form onSubmit={(e) => { e.preventDefault(); void submitPassword(); }}>
            <p className="om-visit__note"><b>{email}</b> 계정이 있습니다.</p>
            <label className="om-field"><span>비밀번호</span>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password" required /></label>
            <Button type="submit" variant="primary" block disabled={busy || !password}>
              {busy ? "로그인 중…" : "로그인"}
            </Button>
          </form>
        ) : step === "code" ? (
          <form onSubmit={(e) => { e.preventDefault(); void submitCode(); }}>
            <p className="om-visit__note"><b>{email}</b> 로 인증 코드를 보냈어요.</p>
            {/*
              * 서버가 `EXPOSE_DEV_VERIFICATION_CODE` 로 막고 있지만, 화면에서도 한 번 더 막는다.
              * 설정이 잘못된 서버가 코드를 흘려도 화면이 그걸 대신 보여줄 이유는 없다.
              */}
            {devCode && import.meta.env.DEV
              ? <p className="om-visit__rm">개발 환경 코드: <b className="num">{devCode}</b></p>
              : null}
            <label className="om-field"><span>인증 코드 6자리</span>
              <input inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)}
                style={{ letterSpacing: ".3em" }} className="num" required /></label>
            <Button type="submit" variant="primary" block disabled={busy || code.trim().length < 6}>
              {busy ? "확인 중…" : "확인"}
            </Button>
          </form>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); void submitName(); }}>
            <label className="om-field"><span>표시 이름</span>
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} required /></label>
            <label className="om-field"><span>비밀번호</span>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password" minLength={8} required /></label>
            <p className="om-visit__note">같은 이름이 있어도 괜찮아요 — 고유 번호가 붙습니다.</p>
            <Button type="submit" variant="primary" block disabled={busy || !displayName.trim() || password.length < 8}>
              {busy ? "만드는 중…" : "시작하기"}
            </Button>
          </form>
        )}

        {error ? <p className="om-note om-note--err" role="alert">{error}</p> : null}
        {step === "email" ? (
          <>
            <p className="om-authsheet__or"><span>또는</span></p>
            <div className="om-authsheet__social">
              {OAUTH_PROVIDERS.map((p) => (
                <a key={p} className="om-authsheet__prov" href={OAUTH.start(p)}
                  onClick={() => session.cancelPending()}>
                  {SOCIAL_LABEL[p]} 로 계속하기
                </a>
              ))}
            </div>
            <p className="om-visit__note" style={{ marginTop: "var(--s-2)" }}>
              소셜 로그인은 이 창을 벗어났다가 돌아옵니다. 하던 동작은 이어지지 않아요.
            </p>
          </>
        ) : null}

        <Button variant="ghost" block style={{ marginTop: "var(--s-3)" }}
          onClick={() => { session.cancelPending(); onClose(); }}>나중에</Button>
      </div>
    </div>
  );
}
